import { ReactNode, useEffect, useState } from 'react';
import { AlertTriangle, Check, Copy, Loader2, X } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

// ---------------------------------------------------------------- formats
export const eur = (cents: number | null | undefined, digits = 2) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: digits, maximumFractionDigits: digits }).format((cents ?? 0) / 100);

export const num = (value: number | null | undefined) => new Intl.NumberFormat('fr-FR').format(value ?? 0);

// Les dates de l'API sont en UTC sans fuseau
const toDate = (value: string) => new Date(/Z|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);

export const dateTime = (value: string | null | undefined) => (value ? format(toDate(value), 'dd/MM/yyyy HH:mm', { locale: fr }) : '—');
export const dateOnly = (value: string | null | undefined) => (value ? format(toDate(value), 'dd/MM/yyyy', { locale: fr }) : '—');
export const ago = (value: string | null | undefined) => (value ? formatDistanceToNow(toDate(value), { addSuffix: true, locale: fr }) : 'jamais');

// ---------------------------------------------------------------- mise en page
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ title, action, children, className = '' }: { title?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>
      {(title || action) && (
        <header className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {action}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function StatCard({ label, value, hint, tone = 'default', icon }: { label: string; value: ReactNode; hint?: ReactNode; tone?: 'default' | 'good' | 'bad' | 'warn'; icon?: ReactNode }) {
  const color = { default: 'text-slate-900', good: 'text-emerald-600', bad: 'text-red-600', warn: 'text-amber-600' }[tone];
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between text-slate-500">
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
        {icon}
      </div>
      <div className={`mt-2 text-2xl font-semibold tabular-nums ${color}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- badges
type Tone = 'slate' | 'green' | 'red' | 'amber' | 'blue' | 'violet';
const tones: Record<Tone, string> = {
  slate: 'bg-slate-100 text-slate-700 ring-slate-200',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  red: 'bg-red-50 text-red-700 ring-red-200',
  amber: 'bg-amber-50 text-amber-700 ring-amber-200',
  blue: 'bg-sky-50 text-sky-700 ring-sky-200',
  violet: 'bg-violet-50 text-violet-700 ring-violet-200',
};

export function Badge({ tone = 'slate', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${tones[tone]}`}>{children}</span>;
}

const documentStatuses: Record<string, [string, Tone]> = {
  draft: ['Brouillon', 'slate'],
  sent: ['Envoyé', 'blue'],
  in_progress: ['En cours', 'blue'],
  completed: ['Signé', 'green'],
  declined: ['Refusé', 'red'],
  expired: ['Expiré', 'amber'],
  voided: ['Annulé', 'slate'],
};
export const documentStatusLabel = (status: string) => documentStatuses[status]?.[0] ?? status;
export const DocumentStatusBadge = ({ status }: { status: string }) => <Badge tone={documentStatuses[status]?.[1] ?? 'slate'}>{documentStatusLabel(status)}</Badge>;

const signerStatuses: Record<string, [string, Tone]> = {
  pending: ['En attente', 'slate'],
  notified: ['Invité', 'blue'],
  viewed: ['Document consulté', 'blue'],
  authenticated: ['Authentifié', 'violet'],
  signed: ['A signé', 'green'],
  declined: ['A refusé', 'red'],
};
export const SignerStatusBadge = ({ status }: { status: string }) => <Badge tone={signerStatuses[status]?.[1] ?? 'slate'}>{signerStatuses[status]?.[0] ?? status}</Badge>;

// ---------------------------------------------------------------- formulaires
export const inputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:bg-slate-50';

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

type ButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  disabled?: boolean;
  loading?: boolean;
  small?: boolean;
};

export function Button({ children, onClick, type = 'button', variant = 'primary', disabled, loading, small }: ButtonProps) {
  const styles = {
    primary: 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-sm',
    secondary: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
    danger: 'bg-white text-red-600 border border-red-200 hover:bg-red-50',
    ghost: 'text-slate-600 hover:bg-slate-100',
  }[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${small ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} ${styles}`}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 sm:p-8" onMouseDown={onClose}>
      <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} rounded-xl bg-white shadow-xl`} onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- etats
export function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <Loader2 className="w-7 h-7 animate-spin text-indigo-600" />
    </div>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
      <AlertTriangle className="w-5 h-5 shrink-0" />
      <span className="flex-1">{message}</span>
      {onRetry && <Button variant="secondary" small onClick={onRetry}>Réessayer</Button>}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="py-10 text-center text-sm text-slate-500">{children}</div>;
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: string }[]; value: T; onChange: (id: T) => void }) {
  return (
    <div className="mb-5 flex gap-1 border-b border-slate-200">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${value === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/** Affiche un secret une seule fois, avec copie. */
export function SecretBox({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // presse-papiers indisponible : la valeur reste selectionnable
    }
  };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <p className="text-sm font-medium text-amber-900">{label}</p>
      <p className="mt-1 text-xs text-amber-800">Elle n&apos;est affichée qu&apos;une seule fois : copiez-la maintenant et transmettez-la par un canal sûr.</p>
      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 select-all break-all rounded-md bg-white px-3 py-2 font-mono text-xs text-slate-900 ring-1 ring-amber-200">{value}</code>
        <Button variant="secondary" small onClick={copy}>
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copié' : 'Copier'}
        </Button>
      </div>
    </div>
  );
}

export function Table({ head, children }: { head: ReactNode[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            {head.map((cell, index) => (
              <th key={index} className="px-3 py-2.5 first:pl-0 last:pr-0 whitespace-nowrap">{cell}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

export const Td = ({ children, className = '' }: { children?: ReactNode; className?: string }) => (
  <td className={`px-3 py-3 first:pl-0 last:pr-0 text-slate-700 ${className}`}>{children}</td>
);
